import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { UPDATE_TEAM_SHARE_TRANSACTIONS_IN_CMA } from '@modules/reports/cma-reports/cmaReportsQueries';
import type { CMAPageId } from '@modules/reports/shared/utils';

export function useTeamTransactionsSharing(teamId: string | undefined): {
  optIn: () => Promise<void>;
  optOut: () => Promise<void>;
  loading: boolean;
  lastError: ErrorLike | null;
} {
  const { data: cmaReport } = useReportContext<CMAPageId>();
  const cmaReportId = cmaReport?.id;

  const [updateTeamTransactionStatus, { loading, error }] = useMutation(
    UPDATE_TEAM_SHARE_TRANSACTIONS_IN_CMA,
  );

  const lastError = error || null;

  const optIn = async (): Promise<void> => {
    if (!teamId) {
      console.error('No team ID available for opt-in');
      return;
    }

    try {
      await updateTeamTransactionStatus({
        variables: {
          team_id: teamId,
          share_transactions_in_cma: true,
          cma_report_id: cmaReportId,
        },
      });
    } catch (error) {
      console.error('Failed to opt in:', error);
      throw error;
    }
  };

  const optOut = async (): Promise<void> => {
    if (!teamId) {
      console.error('No team ID available for opt-out');
      return;
    }

    try {
      await updateTeamTransactionStatus({
        variables: {
          team_id: teamId,
          share_transactions_in_cma: false,
          cma_report_id: cmaReportId,
        },
      });
    } catch (error) {
      console.error('Failed to opt out:', error);
      throw error;
    }
  };

  return {
    optIn,
    optOut,
    loading,
    lastError,
  };
}
